import React, { useState, useEffect } from 'react';
import { Database, Play, Square, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle, Activity, FileText, Eye, FolderOpen } from 'lucide-react';

// Add custom CSS animations
const styles = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes slide-in {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .animate-shimmer {
    animation: shimmer 2s infinite;
  }

  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`;

const LocationInventoryQuery = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingInProgress, setProcessingInProgress] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [cronExpression, setCronExpression] = useState('0 4 * * *');
  const [logFilter, setLogFilter] = useState('');
  const [logPage, setLogPage] = useState(0);
  const [logPagination, setLogPagination] = useState(null);
  const [toast, setToast] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [loadingErrorDetails, setLoadingErrorDetails] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';

  // Detect user's timezone from browser
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Toast notification helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Fetch configuration and status
  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch config');
      }

      const data = await response.json();
      setConfig(data.data);
      setCronExpression(data.data.schedule.cronExpression || '0 4 * * *');

      // Update processing state
      const currentStatus = data.data.currentStatus.status;
      const previousStatus = config?.currentStatus?.status;

      if (currentStatus === 'running') {
        setProcessingInProgress(true);
      } else {
        setProcessingInProgress(false);

        // Show completion notification if status changed
        if (previousStatus === 'running' && currentStatus !== 'running') {
          if (currentStatus === 'success') {
            showToast('✅ CSV processing completed successfully!', 'success');
          } else if (currentStatus === 'error') {
            showToast('❌ CSV processing failed. Check logs for details.', 'error');
          } else if (currentStatus === 'partial') {
            showToast('⚠️ CSV processing completed with warnings.', 'warning');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch CSV files in directory
  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }

      const data = await response.json();
      setFiles(data.data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  // Fetch logs
  const fetchLogs = async (page = 0, status = '') => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit: 20,
        offset: page * 20
      });

      if (status) {
        params.append('status', status);
      }

      const response = await fetch(`${API_BASE_URL}/location-inventory/logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      setLogs(data.data.logs);
      setLogPagination(data.data.pagination);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchStats(), fetchLogs(), fetchFiles()]);
      setLoading(false);
    };
    loadData();

    // Poll for updates every 5 seconds
    const interval = setInterval(() => {
      fetchConfig();
      if (activeTab === 'logs') {
        fetchLogs(logPage, logFilter);
      }
      if (activeTab === 'files') {
        fetchFiles();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [activeTab, logPage, logFilter]);

  // Manual process trigger
  const handleManualProcess = async () => {
    if (processingInProgress) {
      showToast('Processing is already in progress', 'warning');
      return;
    }

    if (!window.confirm('Process all CSV files in the directory now?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to start processing');
      }

      setProcessingInProgress(true);
      showToast('🚀 CSV processing started successfully', 'success');

      // Refresh data
      setTimeout(() => {
        fetchConfig();
        fetchStats();
        fetchLogs();
        fetchFiles();
      }, 1000);
    } catch (error) {
      showToast(`❌ Failed to start processing: ${error.message}`, 'error');
    }
  };

  // Start automated processing
  const handleStartAutomated = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to enable automated processing');
      }

      alert('✅ Automated processing enabled');
      fetchConfig();
    } catch (error) {
      alert(`❌ Failed to enable automated processing:\n${error.message}`);
    }
  };

  // Stop automated processing
  const handleStopAutomated = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to disable automated processing');
      }

      alert('✅ Automated processing disabled');
      fetchConfig();
    } catch (error) {
      alert(`❌ Failed to disable automated processing:\n${error.message}`);
    }
  };

  // Update schedule
  const handleUpdateSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/schedule`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cronExpression,
          timezone: userTimezone
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update schedule');
      }

      alert('✅ Schedule updated successfully');
      fetchConfig();
    } catch (error) {
      alert(`❌ Failed to update schedule:\n${error.message}`);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      idle: { bg: 'bg-slate-600', text: 'Idle', icon: <Clock className="w-4 h-4" /> },
      running: { bg: 'bg-blue-600', text: 'Running', icon: <Activity className="w-4 h-4 animate-pulse" /> },
      success: { bg: 'bg-green-600', text: 'Success', icon: <CheckCircle className="w-4 h-4" /> },
      error: { bg: 'bg-red-600', text: 'Error', icon: <XCircle className="w-4 h-4" /> },
      partial: { bg: 'bg-yellow-600', text: 'Partial', icon: <AlertCircle className="w-4 h-4" /> },
      stopped: { bg: 'bg-slate-600', text: 'Stopped', icon: <Square className="w-4 h-4" /> }
    };

    const badge = badges[status] || badges.idle;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-white text-sm font-medium ${badge.bg}`}>
        {badge.icon}
        <span>{badge.text}</span>
      </span>
    );
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Fetch error details for modal
  const fetchErrorDetails = async (executionId) => {
    setLoadingErrorDetails(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/location-inventory/logs/${executionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch error details');
      }

      const data = await response.json();
      setErrorModal(data.data);
    } catch (error) {
      console.error('Error fetching error details:', error);
      showToast(`Failed to load error details: ${error.message}`, 'error');
    } finally {
      setLoadingErrorDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-slate-900">
        {/* Toast Notification */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 animate-slide-in">
            <div className={`px-6 py-4 rounded-lg shadow-lg border-l-4 ${
              toast.type === 'success' ? 'bg-green-900 border-green-500' :
              toast.type === 'error' ? 'bg-red-900 border-red-500' :
              toast.type === 'warning' ? 'bg-yellow-900 border-yellow-500' :
              'bg-blue-900 border-blue-500'
            }`}>
              <div className="flex items-center space-x-3">
                {toast.type === 'success' && <CheckCircle className="w-5 h-5 text-green-400" />}
                {toast.type === 'error' && <XCircle className="w-5 h-5 text-red-400" />}
                {toast.type === 'warning' && <AlertCircle className="w-5 h-5 text-yellow-400" />}
                {toast.type === 'info' && <Activity className="w-5 h-5 text-blue-400" />}
                <p className="text-white font-medium">{toast.message}</p>
                <button
                  onClick={() => setToast(null)}
                  className="text-slate-400 hover:text-white"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Details Modal */}
        {errorModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
            <div className="bg-slate-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-slate-700">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
                <div className="flex items-center space-x-3">
                  {errorModal.status === 'success' ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : errorModal.status === 'partial' ? (
                    <AlertCircle className="w-6 h-6 text-yellow-400" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-400" />
                  )}
                  <h2 className="text-xl font-bold text-slate-100">
                    {errorModal.status === 'success' ? 'Execution Details' :
                     errorModal.status === 'partial' ? 'Execution Details (Partial Success)' :
                     'Execution Error Details'}
                  </h2>
                </div>
                <button
                  onClick={() => setErrorModal(null)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>

              {/* Modal Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                {loadingErrorDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
                  </div>
                ) : (
                  <div className="p-6 space-y-6">
                    {/* Error/Success Summary */}
                    <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                      <h3 className="text-lg font-semibold text-slate-100 mb-4">
                        {errorModal.status === 'success' ? 'Processing Summary' : 'Error Details'}
                      </h3>

                      {errorModal.errorMessage && (
                        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 mb-4">
                          <p className="text-red-300 font-medium">{errorModal.errorMessage}</p>
                        </div>
                      )}

                      {errorModal.duplicateLpns && errorModal.duplicateLpns.length > 0 && (
                        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4 mb-4">
                          <p className="text-yellow-300 font-medium mb-2">Duplicate LPNs Found:</p>
                          <ul className="text-yellow-200 text-sm space-y-1">
                            {errorModal.duplicateLpns.map((dup, idx) => (
                              <li key={idx}>• {dup}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-950 rounded-lg p-4">
                          <p className="text-slate-400 text-sm mb-1">Records Processed</p>
                          <p className="text-slate-100 text-2xl font-bold">{errorModal.recordsProcessed?.toLocaleString() || 0}</p>
                        </div>
                        <div className="bg-slate-950 rounded-lg p-4">
                          <p className="text-slate-400 text-sm mb-1">Execution Time</p>
                          <p className="text-slate-100 text-2xl font-bold">{formatDuration(errorModal.executionTimeMs)}</p>
                        </div>
                      </div>

                      {errorModal.csvFilename && (
                        <div className="mt-4 bg-slate-950 rounded-lg p-3">
                          <p className="text-slate-400 text-sm">Processed File</p>
                          <p className="text-slate-100 font-mono text-sm">{errorModal.csvFilename}</p>
                        </div>
                      )}
                    </div>

                    {/* Execution Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                        <p className="text-slate-400">Started At</p>
                        <p className="text-slate-100 font-mono">{formatTimestamp(errorModal.startedAt)}</p>
                      </div>
                      <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                        <p className="text-slate-400">Completed At</p>
                        <p className="text-slate-100 font-mono">{formatTimestamp(errorModal.completedAt)}</p>
                      </div>
                      <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                        <p className="text-slate-400">Execution ID</p>
                        <p className="text-slate-100 font-mono text-xs">{errorModal.executionId}</p>
                      </div>
                      <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                        <p className="text-slate-400">Encoding Used</p>
                        <p className="text-slate-100 font-mono">{errorModal.encodingUsed || 'utf-8'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-slate-700 bg-slate-900/50">
                <button
                  onClick={() => setErrorModal(null)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <FolderOpen className="w-10 h-10 text-cyan-400" />
              <h1 className="text-3xl font-bold text-slate-100">
                Location Inventory Query
              </h1>
            </div>
            <p className="text-lg text-slate-400">
              Automated CSV file processing from SAMBA share to MySQL database
            </p>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Current Status</span>
                <Activity className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="mt-2">
                {config && getStatusBadge(config.currentStatus.status)}
              </div>
              {config?.currentStatus.operation && (
                <p className="text-slate-500 text-xs mt-2">{config.currentStatus.operation}</p>
              )}
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Total Processed</span>
                <RefreshCw className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold text-slate-100">
                {stats?.totalProcessed || 0}
              </div>
              <p className="text-slate-500 text-xs mt-1">
                Success Rate: {stats?.successRate || 0}%
              </p>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">CSV Files Pending</span>
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold text-slate-100">
                {files.length}
              </div>
              <p className="text-slate-500 text-xs mt-1">
                In rk_csv_imports directory
              </p>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Avg. Duration</span>
                <Clock className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold text-slate-100">
                {formatDuration(stats?.avgExecutionTimeMs)}
              </div>
              <p className="text-slate-500 text-xs mt-1">
                Last run: {stats?.lastProcessTime ? formatTimestamp(stats.lastProcessTime) : 'Never'}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-slate-700">
              <nav className="-mb-px flex space-x-8">
                {['overview', 'files', 'schedule', 'logs'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                      activeTab === tab
                        ? 'border-cyan-500 text-cyan-400'
                        : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Control Panel */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-slate-100 mb-4">Control Panel</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={handleManualProcess}
                    disabled={processingInProgress}
                    className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium ${
                      processingInProgress
                        ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                    }`}
                  >
                    <Play className="w-5 h-5" />
                    <span>Process CSV Files</span>
                  </button>

                  <button
                    onClick={() => fetchFiles()}
                    className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Refresh File List</span>
                  </button>
                </div>

                {config?.currentStatus.status === 'running' && (
                  <div className="mt-6">
                    {/* Animated loading indicator */}
                    <div className="flex items-center justify-center mb-4">
                      <div className="relative">
                        <div className="w-16 h-16 border-4 border-slate-700 border-t-cyan-500 rounded-full animate-spin"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <RefreshCw className="w-6 h-6 text-cyan-400 animate-pulse" />
                        </div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {config.currentStatus.progressPercentage > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-slate-400 mb-2">
                          <span className="font-medium">{config.currentStatus.operation || 'Processing...'}</span>
                          <span className="font-bold text-cyan-400">{config.currentStatus.progressPercentage}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden relative">
                          <div
                            className="bg-gradient-to-r from-cyan-500 to-blue-500 h-3 rounded-full transition-all duration-500 ease-out relative"
                            style={{ width: `${config.currentStatus.progressPercentage}%` }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-slate-400 text-sm">
                            <span className="text-cyan-400 font-semibold">
                              {config.currentStatus.recordsProcessed?.toLocaleString() || 0}
                            </span>
                            {' '}records processed
                          </p>
                          <p className="text-slate-500 text-xs">
                            Started: {config.currentStatus.startedAt ? formatTimestamp(config.currentStatus.startedAt) : 'Just now'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Configuration Info */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-slate-100 mb-4">Configuration</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">CSV Source Directory</p>
                    <p className="text-slate-100 font-mono">/var/www/project-ksp-hq/project/rk_csv_imports</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Target Database</p>
                    <p className="text-slate-100 font-mono">Akamai MySQL</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Target Table</p>
                    <p className="text-slate-100 font-mono">Location_Inventory_Query</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Poll Interval</p>
                    <p className="text-slate-100 font-mono">30 seconds</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-100">CSV Files in Directory</h2>
                <button
                  onClick={fetchFiles}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </button>
              </div>

              {files.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No CSV files found in directory</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Filename</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Size</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Modified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file, idx) => (
                        <tr key={idx} className="border-b border-slate-700 hover:bg-slate-700/50">
                          <td className="py-3 px-4 text-slate-300 font-mono">{file.name}</td>
                          <td className="py-3 px-4 text-right text-slate-300">{formatFileSize(file.size)}</td>
                          <td className="py-3 px-4 text-right text-slate-300">{formatTimestamp(file.modified)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Schedule Configuration</h2>

              <div className="space-y-6">
                {/* Schedule Status */}
                <div className="flex items-center justify-between p-4 bg-slate-900 rounded-lg">
                  <div>
                    <p className="text-slate-100 font-medium">Automated Processing</p>
                    <p className="text-slate-400 text-sm">
                      {config?.schedule.isEnabled ? 'Currently enabled' : 'Currently disabled'}
                    </p>
                    {config?.schedule.nextRun && config?.schedule.isEnabled && (
                      <p className="text-slate-500 text-xs mt-1">
                        Next run: {formatTimestamp(config.schedule.nextRun)}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={config?.schedule.isEnabled ? handleStopAutomated : handleStartAutomated}
                    className={`px-6 py-2 rounded-lg font-medium ${
                      config?.schedule.isEnabled
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {config?.schedule.isEnabled ? 'Disable' : 'Enable'}
                  </button>
                </div>

                {/* Cron Expression */}
                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Cron Expression
                  </label>
                  <div className="flex space-x-4">
                    <input
                      type="text"
                      value={cronExpression}
                      onChange={(e) => setCronExpression(e.target.value)}
                      className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 font-mono focus:outline-none focus:border-cyan-500"
                      placeholder="0 4 * * *"
                    />
                    <button
                      onClick={handleUpdateSchedule}
                      className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium"
                    >
                      Update
                    </button>
                  </div>
                  <p className="text-slate-500 text-xs mt-2">
                    Current schedule: {config?.schedule.cronExpression}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    Your timezone: {userTimezone} • Server timezone: {config?.schedule.timezone || 'Not set'}
                  </p>
                </div>

                {/* Preset Schedules */}
                <div>
                  <p className="text-slate-300 font-medium mb-3">Quick Presets (in your local time)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Every 30 min', cron: '*/30 * * * *' },
                      { label: 'Every hour', cron: '0 * * * *' },
                      { label: 'Every 6 hours', cron: '0 */6 * * *' },
                      { label: 'Daily at 4 AM', cron: '0 4 * * *' }
                    ].map((preset) => (
                      <button
                        key={preset.cron}
                        onClick={() => setCronExpression(preset.cron)}
                        className="px-4 py-2 bg-slate-900 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-300 text-sm"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Last Run Info */}
                {config?.schedule.lastRun && (
                  <div className="p-4 bg-slate-900 rounded-lg">
                    <p className="text-slate-400 text-sm">Last Scheduled Run</p>
                    <p className="text-slate-100 font-medium">{formatTimestamp(config.schedule.lastRun)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-100">Execution Logs</h2>
                <select
                  value={logFilter}
                  onChange={(e) => {
                    setLogFilter(e.target.value);
                    setLogPage(0);
                    fetchLogs(0, e.target.value);
                  }}
                  className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                  <option value="partial">Partial</option>
                  <option value="running">Running</option>
                </select>
              </div>

              {/* Logs Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Started</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">CSV File</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Records</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Duration</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-slate-500">
                          No logs found
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => (
                        <tr key={log.id} className="border-b border-slate-700 hover:bg-slate-700/50">
                          <td className="py-3 px-4 text-slate-300">
                            {formatTimestamp(log.started_at)}
                          </td>
                          <td className="py-3 px-4">
                            {getStatusBadge(log.status)}
                          </td>
                          <td className="py-3 px-4 text-slate-300 font-mono text-xs">
                            {log.csv_filename || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-right text-slate-300">
                            {log.records_processed?.toLocaleString() || 0}
                          </td>
                          <td className="py-3 px-4 text-right text-slate-300">
                            {formatDuration(log.execution_time_ms)}
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => fetchErrorDetails(log.execution_id)}
                              className={`flex items-center space-x-2 px-3 py-1 rounded text-sm transition-colors ${
                                log.status === 'error' || log.status === 'partial'
                                  ? 'bg-red-900/30 hover:bg-red-900/50 border border-red-700/50 text-red-300'
                                  : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                              }`}
                            >
                              <Eye className="w-4 h-4" />
                              <span>{log.status === 'error' || log.status === 'partial' ? 'View Error' : 'View Details'}</span>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {logPagination && logPagination.total > 20 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                  <p className="text-slate-400 text-sm">
                    Showing {logPagination.offset + 1} - {Math.min(logPagination.offset + logPagination.limit, logPagination.total)} of {logPagination.total}
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const newPage = logPage - 1;
                        setLogPage(newPage);
                        fetchLogs(newPage, logFilter);
                      }}
                      disabled={logPage === 0}
                      className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        const newPage = logPage + 1;
                        setLogPage(newPage);
                        fetchLogs(newPage, logFilter);
                      }}
                      disabled={!logPagination.hasMore}
                      className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LocationInventoryQuery;
